import { motion } from "framer-motion";
import { BedDouble, ShowerHead, Coffee, Croissant, Trees, Flame, Wifi, MapPinned, ExternalLink, MessageCircle, Footprints, Bike, Play, Church, Landmark, Signpost } from "lucide-react";
import { useLang, scrollToId } from "@/i18n";
import { waLink } from "@/api";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 30 }, whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" }, transition: { duration: 0.8, delay },
});

const SIMPLE_ICONS = [BedDouble, ShowerHead, Coffee, Croissant, Trees, Flame, Wifi, MapPinned];
const TIMELINE_ICONS = [Signpost, Footprints, Trees, Trees, Church, Signpost, Landmark, Landmark, Landmark];

const LINKS = {
  stage: "https://www.viatransilvanica.com/ro/tinuturi/terra-borza-teutonica/rasnov-brasov",
  guide: "https://www.viatransilvanica.com/ro/ghid-vt",
  video: "https://www.youtube.com/watch?v=LTuIDyfs3HY&list=PLWdNel3JWrXl0FnMQdNVwhsWz09eHdcAl",
};

export default function ViaTransilvanica({ config }) {
  const { t } = useLang();
  const wa = config?.whatsapp || "40721663181";
  const simple = t("vt.simple");
  const timeline = t("vt.timeline");

  return (
    <section id="via-transilvanica" className="py-24 md:py-32" data-testid="via-transilvanica">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <motion.p {...fadeUp()} className="text-xs md:text-sm tracking-[0.25em] uppercase font-bold text-walnut flex items-center gap-3" data-testid="vt-eyebrow">
          <span className="inline-block w-10 h-px bg-terracotta" />
          🥾 {t("vt.eyebrow")}
        </motion.p>

        <div className="mt-8 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <motion.h2 {...fadeUp(0.1)} className="font-serif font-medium text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-main" data-testid="vt-title">
              {t("vt.title")}
            </motion.h2>
            <motion.p {...fadeUp(0.15)} className="mt-4 font-serif italic text-2xl md:text-3xl text-terracotta" data-testid="vt-sub">
              {t("vt.sub")}
            </motion.p>
            <motion.p {...fadeUp(0.2)} className="mt-6 text-brown/85 font-light leading-relaxed" data-testid="vt-lead">
              {t("vt.lead")}
            </motion.p>
            <motion.blockquote {...fadeUp(0.25)} className="mt-8 border-l-2 border-terracotta pl-6" data-testid="vt-brand-quote">
              <p className="font-serif text-xl md:text-2xl text-green-dark leading-snug">„{t("vt.brand_quote")}"</p>
              <p className="mt-3 text-sm text-brown/70 font-light">{t("vt.brand_quote2")}</p>
            </motion.blockquote>
          </div>
          <motion.div {...fadeUp(0.2)} className="lg:col-span-6">
            <div className="overflow-hidden rounded-3xl border border-warm-grey/40 shadow-xl shadow-main/5">
              <img src="/assets/via-transilvanica.jpg" alt="Drumeți pe Via Transilvanica apropiindu-se de Imperfect House, Râșnov" loading="lazy"
                className="w-full h-auto" data-testid="vt-image" />
            </div>
          </motion.div>
        </div>

        {/* Lucrurile simple */}
        <motion.h3 {...fadeUp()} className="mt-20 font-serif text-2xl md:text-3xl text-main text-center" data-testid="vt-simple-title">
          {t("vt.simple_title")}
        </motion.h3>
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          {simple.map(([title, sub], i) => {
            const Icon = SIMPLE_ICONS[i] || MapPinned;
            return (
              <motion.div key={title} {...fadeUp(0.05 + (i % 4) * 0.07)}
                className="bg-cream-light border border-warm-grey/40 rounded-2xl p-5 text-center"
                data-testid={`vt-simple-${i}`}>
                <Icon size={22} className="mx-auto text-terracotta" />
                <p className="mt-3 font-semibold text-main text-sm">{title}</p>
                <p className="mt-1 text-xs text-brown/70 font-light">{sub}</p>
              </motion.div>
            );
          })}
        </div>

        {/* Povestea etapei + timeline */}
        <div className="mt-24 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <motion.h3 {...fadeUp()} className="font-serif text-3xl md:text-4xl text-main" data-testid="vt-story-title">{t("vt.story_title")}</motion.h3>
            <motion.p {...fadeUp(0.05)} className="mt-2 font-serif italic text-lg text-terracotta">{t("vt.story_sub")}</motion.p>
            <motion.p {...fadeUp(0.1)} className="mt-5 text-brown/85 font-light leading-relaxed" data-testid="vt-story">{t("vt.story")}</motion.p>

            <motion.div {...fadeUp(0.15)} className="mt-8">
              <a href={LINKS.stage} target="_blank" rel="noreferrer" data-testid="vt-stage-link"
                className="inline-flex items-center gap-2 rounded-full bg-main text-cream-light font-semibold px-6 py-3.5 hover:bg-terracotta transition-all duration-300 text-sm">
                {t("vt.stage_cta")} <ExternalLink size={15} />
              </a>
              <p className="mt-3 text-xs text-brown/60 font-light max-w-md">{t("vt.stage_note")}</p>
            </motion.div>
          </div>

          <motion.ol {...fadeUp(0.2)} className="lg:col-span-5 bg-cream-light border border-warm-grey/40 rounded-3xl p-7" data-testid="vt-timeline">
            {timeline.map((stop, i) => {
              const Icon = TIMELINE_ICONS[i] || Signpost;
              const last = i === timeline.length - 1;
              return (
                <li key={stop} className="relative flex gap-4 pb-5 last:pb-0">
                  {!last && <span className="absolute left-[13px] top-7 bottom-0 border-l-2 border-dotted border-terracotta/40" />}
                  <span className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${i === 0 || last ? "bg-terracotta text-cream-light" : "bg-sage/25 text-green-dark"}`}>
                    <Icon size={13} />
                  </span>
                  <span className={`text-sm ${i === 0 || last ? "font-bold text-main" : "text-brown/85 font-light"}`}>{stop}</span>
                </li>
              );
            })}
          </motion.ol>
        </div>

        {/* Ghiduri + video */}
        <div className="mt-16 grid md:grid-cols-3 gap-5">
          {[
            { icon: Footprints, title: t("vt.guide_title"), text: t("vt.guide_text"), cta: t("vt.guide_cta"), href: LINKS.guide, testid: "vt-guide" },
            { icon: Bike, title: t("vt.bike_title"), text: t("vt.bike_text"), cta: t("vt.bike_cta"), href: LINKS.guide, testid: "vt-bike", note: t("vt.bike_note") },
            { icon: Play, title: t("vt.video_title"), text: t("vt.video_text"), cta: t("vt.video_cta"), href: LINKS.video, testid: "vt-video" },
          ].map((c) => (
            <motion.article key={c.testid} {...fadeUp(0.1)} className="bg-cream-light border border-warm-grey/40 rounded-3xl p-7 flex flex-col" data-testid={c.testid}>
              <c.icon size={22} className="text-terracotta" />
              <h4 className="mt-4 font-serif text-xl text-main leading-snug">{c.title}</h4>
              <p className="mt-3 text-sm text-brown/80 font-light leading-relaxed flex-1">{c.text}</p>
              <a href={c.href} target="_blank" rel="noreferrer" data-testid={`${c.testid}-cta`}
                className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-terracotta hover:text-main transition-colors">
                {c.cta} <ExternalLink size={14} />
              </a>
              {c.note && <p className="mt-2 text-[11px] text-brown/60 font-light">{c.note}</p>}
            </motion.article>
          ))}
        </div>

        {/* CTA rezervare */}
        <motion.div {...fadeUp(0.1)} className="mt-20 bg-main text-cream rounded-3xl p-10 md:p-14 text-center relative overflow-hidden grain" data-testid="vt-cta">
          <h3 className="font-serif text-3xl md:text-5xl leading-tight relative z-10">{t("vt.cta_title")}</h3>
          <p className="mt-5 text-cream/75 font-light max-w-xl mx-auto relative z-10">{t("vt.cta_text")}</p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4 relative z-10">
            <button onClick={() => scrollToId("#rezervare")} data-testid="vt-cta-book"
              className="rounded-full bg-terracotta text-cream-light font-semibold px-8 py-4 hover:bg-terracotta/90 transition-all duration-300">
              {t("vt.cta_book")}
            </button>
            <a href={waLink(wa, t("vt.wa_msg"))} target="_blank" rel="noreferrer" data-testid="vt-cta-wa"
              className="rounded-full border border-cream/30 text-cream font-semibold px-8 py-4 hover:border-terracotta hover:text-terracotta transition-all duration-300 inline-flex items-center gap-2">
              <MessageCircle size={17} /> {t("vt.cta_wa")}
            </a>
          </div>
        </motion.div>

        {/* Final + sursa */}
        <motion.div {...fadeUp(0.1)} className="mt-16 text-center" data-testid="vt-final">
          <p className="font-serif text-2xl md:text-3xl text-main">{t("vt.final1")}</p>
          <p className="mt-1 font-serif italic text-2xl md:text-3xl text-terracotta">{t("vt.final2")}</p>
          <p className="mt-4 text-sm text-brown/70 font-light">{t("vt.final3")}</p>
          <p className="mt-8 text-[11px] text-brown/50 font-light max-w-lg mx-auto" data-testid="vt-source-note">{t("vt.source_note")}</p>
        </motion.div>
      </div>
    </section>
  );
}
